import {
  createAsyncThunk,
  createSelector,
  createSlice,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { getStations } from "../api/kquake";

const stationsAdapter = createEntityAdapter({
  selectId: (station) => station.code,
  sortComparer: (a, b) => a.code.localeCompare(b.code),
});

const initialState = stationsAdapter.getInitialState({
  fetchStatus: "idle",
  fetchError: null,
  mapFilter: {
    accelerometer: true,
    broadband: true,
    shortperiod: true,
    // sensorOnGround: true,
    // sensorInBorehole: true,
  },
  mapBounds: {
    north: 90,
    south: -90,
    east: 180,
    west: -180,
  },
});

export const SURFACE_SENSOR_MODELS = [
  "STS-2",
  "STS-2.5",
  "CMG-3T",
  "CMG-40T-1",
  "ES-T",
  "ES-T2",
  "CMG-5T",
];
export const BOREHOLE_SENSOR_MODELS = ["STS-5A", "CMG-3TB", "ES-DH"];
export const fetchStations = createAsyncThunk(
  "stations/fetchStations",
  async (_, { getState }) => {
    const response = await getStations();
    response.forEach((station) => {
      const activeChannels = station.end_date
        ? station.channels
        : station.channels.filter((channel) => !channel.end_date);
      station.sensors = [
        ...new Set(activeChannels.map((channel) => channel.sensor_type)),
      ];
      station.sensorsInKorean = [];
      if (station.sensors.includes("accelerometer")) {
        station.sensorsInKorean.push("가속도");
      }
      if (station.sensors.includes("broadband")) {
        station.sensorsInKorean.push("광대역");
      }
      if (station.sensors.includes("shortperiod")) {
        station.sensorsInKorean.push("단주기");
      }
      const activeSensorModels = [
        ...new Set(activeChannels.map((channel) => channel.sensor)),
      ];
      station.hasSensorOnGround = activeSensorModels.some((sensorModel) =>
        SURFACE_SENSOR_MODELS.includes(sensorModel)
      );
      station.hasSensorInBorehole = activeSensorModels.some((sensorModel) =>
        BOREHOLE_SENSOR_MODELS.includes(sensorModel)
      );
      station.stationType =
        station.hasSensorOnGround && station.hasSensorInBorehole
          ? "surfaceAndBoreholeType"
          : station.hasSensorOnGround
          ? "surfaceType"
          : station.hasSensorInBorehole
          ? "boreholeType"
          : "";
    });
    return response;
  }
);

const stationsSlice = createSlice({
  name: "stations",
  initialState,
  reducers: {
    mapFilterChanged(state, action) {
      const { name, value } = action.payload;
      if (state.mapFilter.hasOwnProperty(name)) {
        state.mapFilter[name] = value;
      }
    },
    mapBoundsChanged(state, action) {
      state.mapBounds = { ...action.payload };
    },
  },
  extraReducers: {
    [fetchStations.pending]: (state, action) => {
      state.fetchStatus = "loading";
    },
    [fetchStations.fulfilled]: (state, action) => {
      state.fetchStatus = "succeeded";
      stationsAdapter.upsertMany(state, action.payload);
    },
    [fetchStations.rejected]: (state, action) => {
      state.fetchStatus = "failed";
      console.error("api failed!");
      state.fetchError = action.error.message;
      const alertMessage = [
        "관측소 정보를 불러오는데 실패하였습니다.",
        "일부 서비스를 정상적으로 이용하실 수 없습니다.",
        "페이지를 새로고침 해보시고 문제가 지속되면 관리자에게 문의하세요.",
      ];
      alert(alertMessage.join("\r\n"));
    },
  },
});

// Action creators are generated for each case reducer function
export const { mapBoundsChanged, mapFilterChanged } = stationsSlice.actions;

export default stationsSlice.reducer;

export const {
  selectAll: selectAllStations,
  selectById: selectStationById,
  selectIds: selectStationIds,
} = stationsAdapter.getSelectors((state) => state.stations);
// export const selectFetchStatus = stationsAdapter.getSelectors((state) => state.fetchStatus)

// const selectEnabledMapFilterItems = (state) => Object.keys(state.stations.mapFilter).filter(key => state.mapFilter[key])

export const selectActiveStations = createSelector(
  [selectAllStations],
  (stations) => stations.filter((station) => !station.end_date)
);

export const selectFilteredStations = createSelector(
  [
    selectAllStations,
    (state) =>
      Object.keys(state.stations.mapFilter).filter(
        (key) => state.stations.mapFilter[key]
      ),
  ],
  (stations, enabledMapFilterItems) =>
    stations.filter((station) =>
      station.sensors.some((sensor) => enabledMapFilterItems.includes(sensor))
    )
);
export const selectFilteredAndVisibleStations = createSelector(
  [selectFilteredStations, (state) => state.stations.mapBounds],
  // (stations, {north, south, east, west}) => {
  //   console.log('selectFilteredAndVisibleStations: stations =', stations)
  //   console.log('selectFilteredAndVisibleStations: north, south, east, west =', north, south, east, west)
  //   return stations.filter(({latitude, longitude}) => latitude >= south && latitude <= north && longitude >= west && longitude <= east)
  // }
  (stations, { north, south, east, west }) =>
    stations.filter(
      ({ latitude, longitude }) =>
        latitude >= south &&
        latitude <= north &&
        longitude >= west &&
        longitude <= east
    )
);

export const selectStationsByBoxRange = createSelector(
  [selectAllStations, (state, filter) => filter],
  (stations, { minLat, maxLat, minLon, maxLon }) => {
    const _minLat = minLat ? minLat : minLat === 0 ? 0 : -90;
    const _maxLat = maxLat ? maxLat : maxLat === 0 ? 0 : 90;
    const _minLon = minLon ? minLon : minLon === 0 ? 0 : -180;
    const _maxLon = maxLon ? maxLon : maxLon === 0 ? 0 : 180;
    return stations.filter(
      ({ latitude, longitude }) =>
        latitude >= _minLat &&
        latitude <= _maxLat &&
        longitude >= _minLon &&
        longitude <= _maxLon
    );
  }
);

// https://en.wikipedia.org/wiki/Earth_radius
const AVERAGE_EARTH_RADIUS_IN_KM = 6371;
const calculateDistance = (loc1, loc2) => {
  const toRadians = (value) => {
    return (value * Math.PI) / 180;
  };
  const dLat = toRadians(loc2.latitude - loc1.latitude);
  const dLon = toRadians(loc2.longitude - loc1.longitude);
  const lat1 = toRadians(loc1.latitude);
  const lat2 = toRadians(loc2.latitude);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = AVERAGE_EARTH_RADIUS_IN_KM * c;
  return d;
};

// https://www.thoughtco.com/degree-of-latitude-and-longitude-distance-4070616
const SIMPLE_KM_TO_DEGREE_OF_LATITUDE_CONVERSION_FACTOR = 112; // 110.567 ~ 111.699
export const selectStationsByCircleRange = createSelector(
  [(state) => state, (state, filter) => filter],
  (state, { centralLat, centralLon, maxRadius }) => {
    const boxRangeFilter = {
      minLat:
        centralLat -
        maxRadius / SIMPLE_KM_TO_DEGREE_OF_LATITUDE_CONVERSION_FACTOR,
      maxLat:
        centralLat +
        maxRadius / SIMPLE_KM_TO_DEGREE_OF_LATITUDE_CONVERSION_FACTOR,
      // Do not filter by latitude because it requires additional complex calculations
      minLon: -180,
      maxLon: 180,
    };
    const boxRangeFilteredStations = selectStationsByBoxRange(
      state,
      boxRangeFilter
    );
    return boxRangeFilteredStations.filter(
      ({ latitude, longitude }) =>
        calculateDistance(
          { latitude, longitude },
          { latitude: centralLat, longitude: centralLon }
        ) <= maxRadius
    );
  }
);
